import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import FormSection from '../components/form/FormSection';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import { labelCls, errorCls, hintCls, controlCls, numberCls } from '../components/form/formStyles';
import { PURPOSES, toOptions, toLocalInput } from '../vmsConstants';
import { normalizePhone, isValidPhone, isValidEmail, LIMITS } from '../visitorConstants';

const PURPOSE_OPTIONS = toOptions(PURPOSES);

// Spelled out rather than humanised, because what each mode does at the gate is
// not obvious from its name.
const MODE_OPTIONS = [
    { value: 'SIMPLE', label: 'Simple — approved on arrival' },
    { value: 'OTP', label: 'OTP — visitor must be verified at the gate' },
    { value: 'NONE', label: 'None — no verification step' },
];

const EMPTY = {
    visitor_name: '',
    visitor_phone: '',
    visitor_email: '',
    wing_id: '',
    flat_id: '',
    purpose: 'PERSONAL',
    purpose_detail: '',
    verification_mode: 'SIMPLE',
    valid_from: '',
    valid_until: '',
    max_usage_count: 1,
    vehicle_number: '',
    companion_count: 0,
    instructions: '',
};

// Create/edit form for a pre-approval. The parent owns submission.
//
// Two rules the API does not enforce are enforced here instead: the validity
// window must run forwards (an inverted one is accepted and then never matches
// anything), and the society's max_pre_approval_days cap is a client-side
// policy because no backend code reads that setting.
const PreApprovalForm = ({
    societyId,
    initial,
    mode = 'create',
    submitting = false,
    maxDays,
    onSubmit,
    onCancel,
}) => {
    const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (initial) setForm(f => ({ ...f, ...initial }));
    }, [initial]);

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.visitor_name.trim()) e.visitor_name = 'Visitor name is required';
        if (!form.visitor_phone.trim()) e.visitor_phone = 'Phone number is required';
        else if (!isValidPhone(form.visitor_phone)) e.visitor_phone = 'Enter a 10-digit mobile number, or a full number with country code';
        if (form.visitor_email.trim() && !isValidEmail(form.visitor_email)) e.visitor_email = 'Enter a valid email address';

        if (mode === 'create') {
            if (!form.wing_id) e.wing_id = 'Wing is required';
            if (!form.flat_id) e.flat_id = 'Flat is required';
        }

        if (!form.valid_from) e.valid_from = 'Start of the window is required';
        if (!form.valid_until) e.valid_until = 'End of the window is required';

        if (form.valid_from && form.valid_until) {
            const from = new Date(form.valid_from);
            const until = new Date(form.valid_until);
            // The API accepts an inverted window and produces a pre-approval
            // that can never match — so this is the only thing standing between
            // the user and a silently dead record.
            if (until <= from) {
                e.valid_until = 'Must be after the start of the window';
            } else if (maxDays) {
                const days = (until - from) / (1000 * 60 * 60 * 24);
                if (days > maxDays) e.valid_until = `Your society allows at most ${maxDays} days`;
            }
        }

        if (Number(form.max_usage_count) < 1) e.max_usage_count = 'Must be at least 1';
        if (Number(form.companion_count) < 0) e.companion_count = 'Cannot be negative';

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (ev) => {
        ev.preventDefault();
        if (!validate()) return;

        const payload = {
            visitor_name: form.visitor_name.trim(),
            // This phone is the match key at the gate — the visit flow looks up
            // (visitor_phone, flat_id, society_id) — so it must be normalised
            // exactly the way the visitor master normalises it.
            visitor_phone: normalizePhone(form.visitor_phone),
            purpose: form.purpose,
            verification_mode: form.verification_mode,
            valid_from: new Date(form.valid_from).toISOString(),
            valid_until: new Date(form.valid_until).toISOString(),
            max_usage_count: Number(form.max_usage_count) || 1,
            companion_count: Number(form.companion_count) || 0,
        };
        [['visitor_email', form.visitor_email], ['purpose_detail', form.purpose_detail],
         ['vehicle_number', form.vehicle_number], ['instructions', form.instructions]]
            .forEach(([k, v]) => { if (v && v.trim()) payload[k] = v.trim(); });

        // Ownership keys are fixed after creation — the update endpoint rejects
        // them outright rather than ignoring them.
        if (mode === 'create') {
            payload.society_id = Number(societyId);
            payload.wing_id = Number(form.wing_id);
            payload.flat_id = Number(form.flat_id);
        }

        onSubmit(payload);
    };

    return (
        <form onSubmit={handleSubmit} className="tw-space-y-5">
            <FormSection
                icon="mdi:account-clock-outline"
                title="Expected visitor"
                hint="Matched at the gate on phone number and flat — not on a visitor record"
            >
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                    <div>
                        <label className={labelCls}>Name <span className="tw-text-rose-500">*</span></label>
                        <input
                            type="text"
                            value={form.visitor_name}
                            onChange={e => set('visitor_name', e.target.value)}
                            maxLength={LIMITS.name}
                            placeholder="e.g. Rajesh Kumar"
                            className={controlCls(!!errors.visitor_name)}
                        />
                        {errors.visitor_name && <p className={errorCls}>{errors.visitor_name}</p>}
                    </div>
                    <div>
                        <label className={labelCls}>Phone <span className="tw-text-rose-500">*</span></label>
                        <input
                            type="tel"
                            inputMode="tel"
                            value={form.visitor_phone}
                            onChange={e => set('visitor_phone', e.target.value)}
                            maxLength={LIMITS.phone}
                            placeholder="9876543210"
                            className={`${controlCls(!!errors.visitor_phone)} tw-font-mono`}
                        />
                        {errors.visitor_phone
                            ? <p className={errorCls}>{errors.visitor_phone}</p>
                            : <p className={hintCls}>The number they will arrive with</p>}
                    </div>
                    <div>
                        <label className={labelCls}>Email</label>
                        <input
                            type="email"
                            value={form.visitor_email}
                            onChange={e => set('visitor_email', e.target.value)}
                            maxLength={LIMITS.email}
                            placeholder="optional"
                            className={controlCls(!!errors.visitor_email)}
                        />
                        {errors.visitor_email && <p className={errorCls}>{errors.visitor_email}</p>}
                    </div>
                </div>
            </FormSection>

            <FormSection icon="mdi:home-outline" title="Flat & purpose">
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                    {mode === 'create' ? (
                        <WingFlatPicker
                            societyId={societyId}
                            wingId={form.wing_id}
                            flatId={form.flat_id}
                            onWingChange={(v) => set('wing_id', v)}
                            onFlatChange={(v) => set('flat_id', v)}
                            errors={errors}
                        />
                    ) : (
                        <div className="sm:tw-col-span-2">
                            <label className={labelCls}>Flat</label>
                            <p className="tw-mb-0 tw-px-3 tw-py-2 tw-text-xs tw-text-gray-600 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-md">
                                {initial?.flatLabel || '—'}
                                <span className="tw-text-gray-400"> · fixed after creation</span>
                            </p>
                        </div>
                    )}
                    <div>
                        <label className={labelCls}>Purpose</label>
                        <SearchableDropdown
                            small
                            clearable={false}
                            value={form.purpose}
                            onChange={(v) => set('purpose', v)}
                            options={PURPOSE_OPTIONS}
                            placeholder="Select purpose"
                        />
                    </div>
                </div>

                <div className="tw-mt-3">
                    <label className={labelCls}>Purpose detail</label>
                    <input
                        type="text"
                        value={form.purpose_detail}
                        onChange={e => set('purpose_detail', e.target.value)}
                        maxLength={255}
                        placeholder="e.g. Birthday party guests"
                        className={controlCls(false)}
                    />
                </div>
            </FormSection>

            <FormSection
                icon="mdi:calendar-range"
                title="Validity"
                hint="Outside this window the pre-approval is skipped at the gate"
            >
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-3">
                    <div>
                        <label className={labelCls}>Valid from <span className="tw-text-rose-500">*</span></label>
                        <input
                            type="datetime-local"
                            value={toLocalInput(form.valid_from) || form.valid_from}
                            onChange={e => set('valid_from', e.target.value)}
                            className={controlCls(!!errors.valid_from)}
                        />
                        {errors.valid_from && <p className={errorCls}>{errors.valid_from}</p>}
                    </div>
                    <div>
                        <label className={labelCls}>Valid until <span className="tw-text-rose-500">*</span></label>
                        <input
                            type="datetime-local"
                            value={toLocalInput(form.valid_until) || form.valid_until}
                            onChange={e => set('valid_until', e.target.value)}
                            className={controlCls(!!errors.valid_until)}
                        />
                        {errors.valid_until && <p className={errorCls}>{errors.valid_until}</p>}
                    </div>
                    <div>
                        <label className={labelCls}>Verification</label>
                        <SearchableDropdown
                            small
                            clearable={false}
                            value={form.verification_mode}
                            onChange={(v) => set('verification_mode', v)}
                            options={MODE_OPTIONS}
                            placeholder="Select mode"
                        />
                    </div>
                    <div>
                        <label className={labelCls}>Allowed entries</label>
                        <input
                            type="number"
                            min={1}
                            value={form.max_usage_count}
                            onChange={e => set('max_usage_count', e.target.value)}
                            className={numberCls(!!errors.max_usage_count)}
                        />
                        {errors.max_usage_count
                            ? <p className={errorCls}>{errors.max_usage_count}</p>
                            : <p className={hintCls}>Marked used after this many</p>}
                    </div>
                </div>

                {form.verification_mode === 'OTP' && (
                    <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2 tw-mt-3 tw-rounded-md tw-bg-amber-50 tw-border tw-border-amber-100 tw-text-[10px] tw-text-amber-800">
                        <Icon icon="mdi:alert-outline" className="tw-text-sm tw-shrink-0 tw-mt-px" />
                        <span>
                            OTP codes are not sent to visitors — no SMS or push exists for them. The gate operator reads
                            the code off the visit and gives it to the visitor, so pick this only if that is how your gate works.
                        </span>
                    </div>
                )}
            </FormSection>

            <FormSection icon="mdi:information-outline" title="Extras">
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                    <div>
                        <label className={labelCls}>Vehicle number</label>
                        <input
                            type="text"
                            value={form.vehicle_number}
                            onChange={e => set('vehicle_number', e.target.value.toUpperCase())}
                            maxLength={20}
                            placeholder="optional"
                            className={`${controlCls(false)} tw-font-mono`}
                        />
                    </div>
                    <div>
                        <label className={labelCls}>Companions</label>
                        <input
                            type="number"
                            min={0}
                            value={form.companion_count}
                            onChange={e => set('companion_count', e.target.value)}
                            className={numberCls(!!errors.companion_count)}
                        />
                        {errors.companion_count && <p className={errorCls}>{errors.companion_count}</p>}
                    </div>
                </div>
                <div className="tw-mt-3">
                    <label className={labelCls}>Instructions for the gate</label>
                    <textarea
                        rows={2}
                        value={form.instructions}
                        onChange={e => set('instructions', e.target.value)}
                        maxLength={1000}
                        placeholder="e.g. Send them up directly, do not call"
                        className={`${controlCls(false)} tw-resize-none`}
                    />
                </div>
            </FormSection>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-3 tw-border-t tw-border-gray-100">
                <button
                    type="button"
                    onClick={onCancel}
                    className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50"
                >
                    <Icon icon="mdi:calendar-check-outline" className="tw-text-base" />
                    {submitting ? 'Saving...' : (mode === 'create' ? 'Create Pre-approval' : 'Save Changes')}
                </button>
            </div>
        </form>
    );
};

export default PreApprovalForm;
